import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { TrainerCard } from '@/features/TrainerLayout/primitives/TrainerCard';
import {
  RubricGrader,
  rubricInvalidIds,
  rubricMarksToNumbers,
  rubricTotal,
  type RubricMarks,
} from '@/features/Trainer/RubricGrader';
import { trainerApi, type PendingSubmission } from '@/lib/api/trainer';
import { queryKeys } from '@/lib/queryKeys';

export const Route = createFileRoute('/trainer/batches/$batchId/grading')({
  component: GradingTab,
});

function GradeForm({
  sub,
  onGraded,
}: {
  sub: PendingSubmission;
  onGraded: () => void;
}) {
  const [score, setScore] = useState('');
  const [marks, setMarks] = useState<RubricMarks>({});
  const [feedback, setFeedback] = useState('');

  // An assessment authored with a marking scheme is graded against it: the
  // single score box would let a trainer type a number the rubric cannot
  // justify, and the student's breakdown would then be unexplainable.
  const rubric = sub.rubric && sub.rubric.criteria.length > 0 ? sub.rubric : null;

  const mutation = useMutation({
    mutationFn: () =>
      trainerApi.gradeSubmission(sub.assessment_id, sub.submission_id, {
        // With a rubric the server derives the score from the marks, so
        // `score` is sent as 0 and ignored rather than being computed twice.
        score: rubric ? 0 : Number(score),
        feedback,
        ...(rubric ? { rubric_scores: rubricMarksToNumbers(marks) } : {}),
      }),
    onSuccess: () => {
      toast.success('Graded successfully');
      onGraded();
    },
    onError: () => toast.error('Failed to save grade'),
  });

  const scoreNum = Number(score);
  const valid = rubric
    ? rubricInvalidIds(rubric, marks).length === 0 &&
      Object.keys(rubricMarksToNumbers(marks)).length > 0 &&
      rubricTotal(marks) <= sub.max_score
    : score !== '' && !isNaN(scoreNum) && scoreNum >= 0 && scoreNum <= sub.max_score;

  return (
    <div className="mt-3 space-y-3 rounded-2xl border border-[#ececec] bg-[#fafafa] p-3">
      {rubric ? (
        <RubricGrader
          rubric={rubric}
          maxScore={sub.max_score}
          marks={marks}
          onChange={setMarks}
          disabled={mutation.isPending}
        />
      ) : (
        <div>
          <label className="mb-1 block text-xs font-medium text-[#6b7280]">
            Score <span className="text-[#111827]">/ {sub.max_score}</span>
          </label>
          <input
            type="number"
            min={0}
            max={sub.max_score}
            value={score}
            onChange={(e) => setScore(e.target.value)}
            placeholder="0"
            className="w-24 rounded-lg border border-[#ececec] bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5a1f]"
          />
        </div>
      )}
      <div>
        <label className="mb-1 block text-xs font-medium text-[#6b7280]">
          Feedback <span className="text-[#9ca3af]">(optional)</span>
        </label>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={3}
          placeholder="Add feedback for the student…"
          className="w-full rounded-lg border border-[#ececec] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5a1f]"
        />
      </div>
      <button
        onClick={() => mutation.mutate()}
        disabled={!valid || mutation.isPending}
        className="rounded-[12px] bg-[#ff5a1f] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
      >
        {mutation.isPending ? 'Saving…' : 'Mark Graded'}
      </button>
    </div>
  );
}

export function GradingTab() {
  const { batchId } = Route.useParams();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: submissions = [], isLoading } = useQuery({
    queryKey: ['pendingSubmissions', batchId],
    queryFn: async () => {
      const { data } = await trainerApi.listPendingSubmissions(batchId);
      return data as PendingSubmission[];
    },
  });

  const handleGraded = (submissionId: string) => {
    setExpandedId(null);
    queryClient.setQueryData<PendingSubmission[]>(
      ['pendingSubmissions', batchId],
      (prev) => (prev ?? []).filter((s) => s.submission_id !== submissionId),
    );
    queryClient.invalidateQueries({ queryKey: queryKeys.trainer.batchOverview(batchId) });
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <p className="mb-4 text-sm text-[#6b7280]">
        {isLoading
          ? 'Loading…'
          : submissions.length === 0
            ? 'All caught up!'
            : `${submissions.length} submission${submissions.length !== 1 ? 's' : ''} to review`}
      </p>

      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#ff5a1f] border-t-transparent" />
        </div>
      ) : submissions.length === 0 ? (
        <TrainerCard className="flex h-32 items-center justify-center p-6 text-sm text-[#6b7280]">
          All caught up! No pending submissions.
        </TrainerCard>
      ) : (
        <div className="space-y-2">
          {submissions.map((sub) => {
            const expanded = expandedId === sub.submission_id;
            const date = new Date(sub.submitted_at);
            const relativeTime = Math.floor((Date.now() - date.getTime()) / 60000);
            const timeLabel =
              relativeTime < 60
                ? `${relativeTime}m ago`
                : relativeTime < 1440
                  ? `${Math.floor(relativeTime / 60)}h ago`
                  : `${Math.floor(relativeTime / 1440)}d ago`;

            return (
              <TrainerCard key={sub.submission_id} className="p-3">
                <button
                  className="flex w-full items-start gap-3 text-left"
                  onClick={() => setExpandedId(expanded ? null : sub.submission_id)}
                >
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#eef0ff] text-xs font-semibold text-[#4b4f8f]">
                    {sub.student_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#111827]">{sub.student_name}</p>
                    <p className="mt-0.5 truncate text-xs text-[#9ca3af]">{sub.assessment_title}</p>
                    <div className="mt-1 flex items-center gap-2">
                      {/* submission_url is nullable since migration 199 — work
                          with no artifact (a viva) has nothing to link to. */}
                      {sub.submission_url ? (
                        <a
                          href={sub.submission_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-0.5 text-xs font-medium text-[#ff5a1f] hover:underline"
                        >
                          View submission <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-xs text-[#9ca3af]">No attached work</span>
                      )}
                      <span className="text-xs text-[#9ca3af]">{timeLabel}</span>
                    </div>
                  </div>
                  {expanded ? (
                    <ChevronUp className="h-4 w-4 flex-shrink-0 text-[#9ca3af]" />
                  ) : (
                    <ChevronDown className="h-4 w-4 flex-shrink-0 text-[#9ca3af]" />
                  )}
                </button>
                {expanded && (
                  <GradeForm
                    sub={sub}
                    onGraded={() => handleGraded(sub.submission_id)}
                  />
                )}
              </TrainerCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
